import React, { useState, useEffect, useRef } from "react";
import { getSalaries } from "./../../helper/Api";
import { useNavigate } from "react-router-dom";
import Spinner from "../../components/Tools/spinner";
import SubHeader from "../../components/Subheader/Subheader";
import { useSelector } from 'react-redux';
import CustomBreadcrumbs from "../../components/Tools/Breadcumbs";
import { toast } from "react-toastify";
import SalaryListAccordion from "./SalaryListAccordion";

const SalaryApprovalList = () => {
  const { financialYears, authData } = useSelector((state) => state.authData);
  const fyIdRef = useRef(localStorage.getItem('globalDateId'));
  const fyList = financialYears.payload.fy_list;
  const [loading, setLoading] = useState(false);
  const globalDateId = localStorage.getItem("globalDateId");
  const [salaryRecords, setSalaryRecords] = useState([]);
  const [grandTotals, setGrandTotals] = useState({ total_gross_amount: 0, total_deduction_amount: 0, total_payable_amount: 0 });
  const navigate = useNavigate();
  const handlePageTrigger = () => {
      fyIdRef.current = localStorage.getItem('globalDateId');
  }
  const formatSalaryList = (rows = []) => {
  const byScheme = new Map();

  const typeKey = (t = "") => {
    const v = String(t).toLowerCase();
    if (v === "agency") return "agencyRecords";
    if (v === "consultant") return "consultantRecords";
    return "employeeRecords"; // default
  };

  const recSort = (a, b) => {
    const at = new Date(a.created_at || 0).getTime();
    const bt = new Date(b.created_at || 0).getTime();
    if (bt !== at) return bt - at;
    return Number(b.id || 0) - Number(a.id || 0);
  };

  // ---- group by scheme
  for (const row of rows) {
    const scheme_id = String(row?.scheme_id ?? "");
    const scheme_name = row?.scheme_title ?? "";

    if (!byScheme.has(scheme_id)) {
      byScheme.set(scheme_id, {
        scheme_id,
        scheme_name,
        agencyRecords: [],
        employeeRecords: [],
        consultantRecords: [],
      });
    }
    byScheme.get(scheme_id)[typeKey(row?.salary_type)].push(row);
  }

  // ---- subhead summaries (unchanged)
  const buildSubheadSummary = (records) => {
    const map = new Map();
    for (const r of records) {
      const id = String(r.subhead_id ?? "");
      if (!map.has(id)) {
        map.set(id, {
          subhead_id: id,
          subhead_name: r.subhead_title ?? "",
          total_gross_amount: 0,
          total_deduction_amount: 0,
          total_payable_amount: 0,
        });
      }
      const obj = map.get(id);
      obj.total_gross_amount += Number(r.gross_amount || 0);
      obj.total_deduction_amount += Number(r.deduction_amount || 0);
      obj.total_payable_amount += Number(r.payable_amount || 0);
    }
    return Array.from(map.values());
  };

  // ---- per-bucket totals (unchanged)
  const buildTotals = (records) =>
    records.reduce(
      (t, r) => {
        t.total_gross_amount += Number(r.gross_amount || 0);
        t.total_deduction_amount += Number(r.deduction_amount || 0);
        t.total_payable_amount += Number(r.payable_amount || 0);
        return t;
      },
      { total_gross_amount: 0, total_deduction_amount: 0, total_payable_amount: 0 }
    );

  // ---- NEW: activity summaries per bucket (group by activity_id)
  const buildActivitySummary = (records) => {
  const map = new Map();

  for (const r of records) {
    const acts = Array.isArray(r.activities) ? r.activities : [];
    if (!acts.length) continue;

    const subheadId = String(r.subhead_id ?? "");
    const subheadName = r.subhead_title ?? "";

    const rowActTotal = acts.reduce((s, a) => s + Number(a?.amount || 0), 0) || 0;
    const rowGross = Number(r.gross_amount || 0);
    const rowDed   = Number(r.deduction_amount || 0);
    const rowPay   = Number(r.payable_amount || 0);

    for (const a of acts) {
      const activityId = String(a.activity_id ?? "");
      const key = `${activityId}__${subheadId}`;

      const aAmt = Number(a.amount || 0);
      const share = rowActTotal > 0 ? aAmt / rowActTotal : 0;

      if (!map.has(key)) {
        map.set(key, {
          activity_id: activityId,
          activity: a.activity ?? "",
          subhead_id: subheadId,
          subhead_name: subheadName,
          total_amount: 0,
          total_gross_amount: 0,
          total_deduction_amount: 0,
          total_payable_amount: 0,
          subactivities: []
        });
      }

      const obj = map.get(key);
      obj.total_amount += aAmt;
      obj.total_gross_amount += rowGross * share;
      obj.total_deduction_amount += rowDed * share;
      obj.total_payable_amount += rowPay * share;

      // subactivity (same proportional share)
      const subId = String(a.subactivity_id ?? "");
      let subObj = obj.subactivities.find(sa => sa.subactivity_id === subId);
      if (!subObj) {
        subObj = {
          subactivity_id: subId,
          subactivity: a.subactivity ?? "",
          total_amount: 0,
          total_gross_amount: 0,
          total_deduction_amount: 0,
          total_payable_amount: 0
        };
        obj.subactivities.push(subObj);
      }

      subObj.total_amount += aAmt;
      subObj.total_gross_amount += rowGross * share;
      subObj.total_deduction_amount += rowDed * share;
      subObj.total_payable_amount += rowPay * share;
    }
  }

  // sort main combos and nested subactivities (optional)
  const sortedActivities = Array.from(map.values()).sort((x, y) =>
    String(x.subhead_name).localeCompare(String(y.subhead_name)) ||
    String(x.activity).localeCompare(String(y.activity))
  );

  sortedActivities.forEach(act => {
    act.subactivities.sort((a, b) =>
      String(a.subactivity).localeCompare(String(b.subactivity))
    );
  });

  return sortedActivities;
};

  // ---- finalize per scheme
  return Array.from(byScheme.values())
    .map((group) => {
      const agencySorted = group.agencyRecords.sort(recSort);
      const employeeSorted = group.employeeRecords.sort(recSort);
      const consultantSorted = group.consultantRecords.sort(recSort);

      const agencyTotals = buildTotals(agencySorted);
      const employeeTotals = buildTotals(employeeSorted);
      const consultantTotals = buildTotals(consultantSorted);

      // GRAND totals at scheme level (unchanged from last step)
      const schemeTotals = {
        total_gross_amount:
          agencyTotals.total_gross_amount +
          employeeTotals.total_gross_amount +
          consultantTotals.total_gross_amount,
        total_deduction_amount:
          agencyTotals.total_deduction_amount +
          employeeTotals.total_deduction_amount +
          consultantTotals.total_deduction_amount,
        total_payable_amount:
          agencyTotals.total_payable_amount +
          employeeTotals.total_payable_amount +
          consultantTotals.total_payable_amount,
      };

      // NEW: activities summaries per category
      const agencyActivitiesSummary = buildActivitySummary(agencySorted);
      const employeeActivitiesSummary = buildActivitySummary(employeeSorted);
      const consultantActivitiesSummary = buildActivitySummary(consultantSorted);

      return {
        // existing fields (kept same order as before as much as possible)
        scheme_id: group.scheme_id,
        scheme_name: group.scheme_name,

        agencyRecords: agencySorted,
        employeeRecords: employeeSorted,
        consultantRecords: consultantSorted,

        agencySubheadSummary: buildSubheadSummary(agencySorted),
        employeeSubheadSummary: buildSubheadSummary(employeeSorted),
        consultantSubheadSummary: buildSubheadSummary(consultantSorted),

        agencyTotals,
        employeeTotals,
        consultantTotals,

        // already added earlier
        schemeTotals,

        // NEW fields
        agencyActivitiesSummary,
        employeeActivitiesSummary,
        consultantActivitiesSummary,
      };
    })
    .sort((a, b) => String(a.scheme_name).localeCompare(String(b.scheme_name)));
}
  const fetchSalaries = async () => {
    setLoading(true);
    try {
      const response = await getSalaries({ fy_id: globalDateId, pending: true});
      if (response.status === 200) {
        const grandTotals = response.data.result.reduce((t, r) => {
                t.total_gross_amount += Number(r.gross_amount || 0);
                t.total_deduction_amount += Number(r.deduction_amount || 0);
                t.total_payable_amount += Number(r.payable_amount || 0);
                return t;
            },
            { total_gross_amount: 0, total_deduction_amount: 0, total_payable_amount: 0 }
        );
        setGrandTotals(grandTotals);
        setSalaryRecords(formatSalaryList(response.data.result));
        setLoading(false);
      }
    } catch (error) {
      setLoading(false);
      toast.error('Something went wrong.', {
        position: toast.POSITION.TOP_CENTER,
      });
    }
  }

  useEffect(() => {
    fetchSalaries();
  }, [globalDateId]);

  return (
    <div>
      {loading && <div > <Spinner/> </div>}
      <SubHeader fy_list={fyList} handlePageTrigger={handlePageTrigger}/>
      <div className="row" style={{ padding: "10px 16px" }}>
          <div className="col-md-6">
              <h4 style={{ color: "#28326f", margin:0}}>Salary Approval</h4>
              <CustomBreadcrumbs linksArray={[]} title="Salary Approval"/>
          </div>
          <div className="col-md-6">
              <div className="m-0 p-0 d-flex gap-2" style={{ width: "auto", float: "right" }}>
                    <button type="button" className="navigation_button" onClick={() => navigate("/salary-payment")}>
                      <span className="fs-14px">Salary Payments</span>
                  </button>
              </div>
          </div>
      </div>
      <div className="row m-0">
        <div className="col-9">
          {/* =================salary card========================= */}
            {
                salaryRecords.map((scheme, index) => (
                    <div
                        className="bg-white  my-2 p-1"
                        key={index}
                    >
                        <div
                        className="bg-prime-20 d-flex justify-content-between align-items-center p-2 transition overflow-hidden px-3"
                        >
                        {/* ======================card header================== */}
                        <span
                            className="fs-14px white-space-nowrap overflow-x-hidden w-100"
                            style={{ fontStyle: "italic" }}
                        >
                            {/* =======================card header====================== */}
                            <span
                            className="fs-14px white-space-nowrap overflow-x-hidden d-flex justify-content-between align-items-center w-100"
                            style={{ width: "95%", fontStyle: "italic" }}
                            >
                            <span className="fw-500 text-secondary-100">
                                {scheme.scheme_name}
                            </span>

                            <i className="icon-grading"></i>
                            </span>
                        </span>
                        </div>

                        {/* ================== card body ========================= */}

                        <div className="mt-2 d-flex flex-column gap-1">
                            <SalaryListAccordion scheme={scheme} authData={authData} isSalary={false} title={"Employee"} records={scheme.employeeRecords} subheadSummary={scheme.employeeSubheadSummary} activitiesSummary={scheme.employeeActivitiesSummary}/>
                            <div className="row bg-info-20 py-1 px-0 m-0 mb-2">
                                <div className="col-4 fs-14px fw-500 m-0 text-start">
                                    Total Gross Amount <br />
                                    {scheme.employeeTotals.total_gross_amount.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                                </div>
                                <div className="col-4 fs-14px fw-500 text-start">
                                    Total Deduction Amount <br />
                                    {scheme.employeeTotals.total_deduction_amount.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                                </div>
                                <div className="col-4 fs-14px fw-500 text-start text-success-100">
                                    Total Payable Amount <br />
                                    {scheme.employeeTotals.total_payable_amount.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                                </div>
                            </div>
                            <SalaryListAccordion scheme={scheme} authData={authData} isSalary={false} title={"Contractual/Consultant"} records={scheme.consultantRecords} subheadSummary={scheme.consultantSubheadSummary} activitiesSummary={scheme.consultantActivitiesSummary}/>
                            <div className="row bg-info-20 py-1 px-0 m-0 mb-2">
                                <div className="col-4 fs-14px fw-500 m-0 text-start">
                                    Total Gross Amount <br />
                                    {scheme.consultantTotals.total_gross_amount.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                                </div>
                                <div className="col-4 fs-14px fw-500 text-start">
                                    Total Deduction Amount <br />
                                    {scheme.consultantTotals.total_deduction_amount.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                                </div>
                                <div className="col-4 fs-14px fw-500 text-start text-success-100">
                                    Total Payable Amount <br />
                                    {scheme.consultantTotals.total_payable_amount.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                                </div>
                            </div>

                            <SalaryListAccordion scheme={scheme} authData={authData} isSalary={true} title={"Salary"} records={scheme.agencyRecords} subheadSummary={scheme.agencySubheadSummary} activitiesSummary={scheme.agencyActivitiesSummary}/>
                            <div className="row bg-info-20 py-1 px-0 m-0 mb-2">
                                <div className="col-4 fs-14px fw-500 m-0 text-start">
                                    Total Gross Amount <br />
                                    {scheme.agencyTotals.total_gross_amount.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                                </div>
                                <div className="col-4 fs-14px fw-500 text-start">
                                    Total Deduction Amount <br />
                                    {scheme.agencyTotals.total_deduction_amount.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                                </div>
                                <div className="col-4 fs-14px fw-500 text-start text-success-100">
                                    Total Payable Amount <br />
                                    {scheme.agencyTotals.total_payable_amount.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                                </div>
                            </div>
                        </div>
                    </div>
                ))
            }

          
        </div>

        {/* =======================right side bar================== */}

        <div className="col-3 px-3 py-2 m-0">
          <div className="totals">
            <div
              className="bg-white p-3 d-flex flex-column"
              style={{
                borderTopLeftRadius: "8px",
                borderTopRightRadius: "8px",
              }}
            >
              <span className="text-success-100 fs-12px">Total Gross Amount</span>
              <span className="text-success-100 fs-20px ">{grandTotals.total_gross_amount.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</span>
            </div>

            <div
              className="bg-white p-3 d-flex flex-column "
              style={{
                borderTop: "1px solid #DFDFDF",
                borderBottom: "1px solid #DFDFDF ",
              }}
            >
              <span className="text-black-60 fs-12px">Total Deduction</span>
              <span className="fs-20px">{grandTotals.total_deduction_amount.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</span>
            </div>

            <div
              className="bg-success-100 p-3 d-flex flex-column "
              style={{
                borderBottomLeftRadius: "8px",
                borderBottomRightRadius: "8px",
              }}
            >
              <span className="text-white fs-12px">Total Payment</span>
              <span className="fs-20px text-white">{grandTotals.total_payable_amount.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SalaryApprovalList;
